// The order of the CSS imports is important.
// If it is in the wrong order, then the classes will not be applied correctly.
// eslint-disable-next-line import/order
import 'ckeditor5/ckeditor5.css';
// eslint-disable-next-line import/order
import 'ckeditor5-premium-features/ckeditor5-premium-features.css';
// eslint-disable-next-line import/order
import './editor.css';
// eslint-disable-next-line import/order
import './plugins/agent-chat-plugin/citation-plugin.css';

import { CKEditor, useCKEditorCloud } from '@ckeditor/ckeditor5-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';

import { UsersIntegration } from '@/components/demands/editor/users-integration';
import { useGetDocumentQuery } from '@/services/api/ckeditorService';
import { useGetDemandTemplateQuery } from '@/services/api/demandService';
import { useGetExportEligibilityQuery } from '@/services/api/proofreadingService';

import { useEditorContext } from './context/editor-provider';
import { ExportOptions, ExportOptionsDialog } from './ExportOptionsDialog';
import CommentsIntegration from './integrations/comments-integration';
import { RevisionHistoryAutosaveIntegration } from './integrations/revision-history-autosave-integration';
import { RevisionHistoryIntegration } from './integrations/revision-history-integration';
import { TrackChangesIntegration } from './integrations/track-changes-integration';
import { AgentChatPlugin } from './plugins/agent-chat-plugin/agent-chat-plugin';
import { CitationPlugin } from './plugins/agent-chat-plugin/citation-plugin';
import { DiffPlugin } from './plugins/agent-chat-plugin/diff-plugin';
import { PortalTarget } from './plugins/common';

const LICENSE_KEY =
  'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3NzQzMTAzOTksImp0aSI6Ijc0YTQ5ZTNhLWQzOWEtNDllMC1hMmUyLTA3NjAwYzUyYTA4YyIsInVzYWdlRW5kcG9pbnQiOiJodHRwczovL3Byb3h5LWV2ZW50LmNrZWRpdG9yLmNvbSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsiY2xvdWQiLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIkFJIiwiRE8iLCJGUCIsIlNDIiwiVE9DIiwiVFBMIiwiUE9FIiwiQ0MiLCJNRiIsIkUyUCIsIkUyVyIsIk1MTCIsIlNFRSIsIlBBRyIsIkNNVCIsIlRDIiwiUkgiLCJSRSIsIlJDTVQiLCJSVEMiLCJSUkgiLCJJVyJdLCJ2YyI6ImJhYzQxZTNmIn0.Q7xIsVNLH0qQ7pgW6kVfeGkZAjWLxDGVhpHoeCywlrDr4tIQddo0kUSgBKauYHS-WnOaoTQA5KO8ChqigTt2mQ';

export const Editor = ({
  firmId,
  matterId,
  documentId,
  workspaceId,
}: {
  firmId: string;
  matterId: string;
  documentId: string;
  workspaceId?: string;
}) => {
  const editorToolbarRef = useRef<HTMLDivElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorRevisionHistoryRef = useRef<HTMLDivElement>(null);
  const editorRevisionHistoryEditorRef = useRef<HTMLDivElement>(null);
  const editorRevisionHistorySidebarRef = useRef<HTMLDivElement>(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const cloud = useCKEditorCloud({ version: '44.3.0', premium: true });
  const [portalTargets, setPortalTargets] = useState<
    Record<string, PortalTarget>
  >({});

  // Export options dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  // Ref to store export options that dataCallback can access
  const exportOptionsRef = useRef<ExportOptions>({
    includeBatesCitations: true,
  });

  const { data: demandTemplate, isLoading: isDemandTemplateLoading } =
    useGetDemandTemplateQuery({ firmId });

  const registerPortalTarget = (portalTarget: PortalTarget) => {
    /**
     * Registers a portal target for the editor.
     * Returns a function to unregister the target.
     */
    const id = uuidv4();
    // eslint-disable-next-line no-console
    console.log('registering portal target', id);
    setPortalTargets((prev) => ({
      ...prev,
      [id]: portalTarget,
    }));

    return () => {
      // eslint-disable-next-line no-console
      console.log('unregistering portal target', id);
      setPortalTargets((prev) => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });
    };
  };

  const { setEditor } = useEditorContext();
  const {
    data: documentData,
    isLoading: isDocumentLoading,
    error: documentError,
  } = useGetDocumentQuery(documentId, {
    pollingInterval: 5000, // Poll every 5 seconds
    skipPollingIfUnfocused: true,
  });
  const editorRef = useRef<any>(null);

  // Gate DOCX export until proofreading eligibility is met
  const [selectedProofJobId, setSelectedProofJobId] = useState<
    string | undefined
  >(undefined);
  const [eligibilityGatingEnabled, setEligibilityGatingEnabled] =
    useState<boolean>(false);
  const [eligibilityRefreshKey, setEligibilityRefreshKey] = useState<number>(0);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { jobId?: string };
      setSelectedProofJobId(detail?.jobId);
    };
    window.addEventListener('proofreading:selected', handler as EventListener);
    const onResponseUpdated = () => setEligibilityRefreshKey((k) => k + 1);
    window.addEventListener(
      'proofreading:response-updated',
      onResponseUpdated as EventListener,
    );
    const onOpen = () => setEligibilityGatingEnabled(true);
    const onClose = () => setEligibilityGatingEnabled(false);
    window.addEventListener('proofreading:open', onOpen as EventListener);
    window.addEventListener('proofreading:close', onClose as EventListener);
    return () => {
      window.removeEventListener(
        'proofreading:selected',
        handler as EventListener,
      );
      window.removeEventListener(
        'proofreading:response-updated',
        onResponseUpdated as EventListener,
      );
      window.removeEventListener('proofreading:open', onOpen as EventListener);
      window.removeEventListener(
        'proofreading:close',
        onClose as EventListener,
      );
    };
  }, []);

  const { data: exportEligibility } = useGetExportEligibilityQuery(
    {
      documentId,
      matterId,
      workspaceId: workspaceId || '',
      jobId: selectedProofJobId,
      refreshKey: eligibilityRefreshKey,
    },
    { skip: !workspaceId || !eligibilityGatingEnabled },
  );

  const areRequiredDependenciesReady = useMemo(() => {
    return !!firmId && !!demandTemplate;
  }, [firmId, demandTemplate]);

  // Handle export with user-selected options
  const handleExportWithOptions = useCallback((options: ExportOptions) => {
    exportOptionsRef.current = options;
    // Execute the actual export command after options are set
    if (editorRef.current) {
      const cmd = editorRef.current.commands.get('exportWord') as any;
      if (cmd && cmd._setBypassDialog) {
        cmd._setBypassDialog(true);
        // Call execute() which will check the bypass flag and run the original
        cmd.execute();
      }
    }
  }, []);

  useEffect(() => {
    if (editorContainerRef.current && areRequiredDependenciesReady) {
      setIsLayoutReady(true);
    }
    return () => setIsLayoutReady(false);
  }, [areRequiredDependenciesReady]);

  const { DecoupledEditor, editorConfig } = useMemo(() => {
    if (!isLayoutReady || cloud.status !== 'success') {
      return {};
    }

    // Standard features
    const {
      Editor: _Editor,
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      Base64UploadAdapter,
      BlockQuote,
      Bold,
      CloudServices,
      Code,
      CodeBlock,
      Essentials,
      FindAndReplace,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      Heading,
      Highlight,
      HorizontalLine,
      HtmlEmbed,
      Image,
      ImageCaption,
      ImageInsert,
      ImageResize,
      ImageStyle,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      Mention,
      PageBreak,
      Paragraph,
      PasteFromOffice,
      RemoveFormat,
      SelectAll,
      SpecialCharacters,
      Strikethrough,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextPartLanguage,
      TextTransformation,
      TodoList,
      Underline,
      WordCount,
      Autosave,
      DecoupledEditor: _DecoupledEditor,
    } = cloud.CKEditor;

    // Premium features
    const {
      CaseChange,
      ExportPdf,
      ExportWord,
      ImportWord,
      MultiLevelList,
      Pagination,
      TableOfContents,
      Template,
      Users,
      RevisionHistory,
      TrackChanges,
      Comments,
    } = cloud.CKEditorPremiumFeatures;

    return {
      DecoupledEditor: _DecoupledEditor,
      editorConfig: {
        plugins: [
          // Essentials
          Essentials,
          Paragraph,
          Autoformat,
          CloudServices,

          // Text formatting
          Bold,
          Italic,
          Underline,
          Strikethrough,
          Code,
          Subscript,
          Superscript,

          // Text styling
          FontFamily,
          FontSize,
          FontColor,
          FontBackgroundColor,
          Highlight,

          // Paragraph formatting
          Alignment,
          Indent,
          IndentBlock,
          BlockQuote,

          // Document structure
          Heading,

          // Lists
          List,
          ListProperties,
          TodoList,
          MultiLevelList,

          // Search & replace
          FindAndReplace,
          SelectAll,

          // Links
          Link,
          AutoLink,

          // Tables
          Table,
          TableToolbar,
          TableProperties,
          TableCellProperties,
          TableCaption,
          TableColumnResize,

          // Media
          Image,
          ImageCaption,
          ImageInsert,
          ImageResize,
          ImageStyle,
          ImageToolbar,
          ImageUpload,
          AutoImage,
          LinkImage,
          MediaEmbed,
          Base64UploadAdapter,

          // Page elements
          HorizontalLine,
          PageBreak,

          // Special features
          SpecialCharacters,
          HtmlEmbed,
          CodeBlock,
          TextPartLanguage,
          RemoveFormat,
          TextTransformation,
          Mention,

          // Import/Export
          PasteFromOffice,
          ExportPdf,
          ExportWord,
          ImportWord,

          // Premium features
          CaseChange,
          TableOfContents,
          Template,

          // Page layout
          Pagination,

          // Utilities
          WordCount,

          // Agent chat plugin
          AgentChatPlugin,
          Users,
          RevisionHistory,
          Autosave,
          TrackChanges,
          Comments,
          DiffPlugin,
          CitationPlugin,
        ],
        extraPlugins: [
          UsersIntegration,
          RevisionHistoryIntegration,
          RevisionHistoryAutosaveIntegration,
          CommentsIntegration,
          TrackChangesIntegration,
        ],
        toolbar: {
          items: [
            'revisionHistory',
            '|',
            'previousPage',
            'nextPage',
            '|',
            // Undo/redo
            'undo',
            'redo',
            '|',

            // Basic formatting (most important)
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'removeFormat',
            '|',

            // Document structure
            'heading',
            'alignment',
            '|',

            // Lists
            'bulletedList',
            'numberedList',
            'todoList',
            'multiLevelList',
            'outdent',
            'indent',
            '|',

            // Links and common media
            'link',
            'insertImage',
            'insertTable',
            '|',

            // Font styling
            'fontSize',
            'fontFamily',
            'fontColor',
            'fontBackgroundColor',
            '|',

            // Special elements
            'blockQuote',
            'horizontalLine',
            '|',

            // Document tools
            'tableOfContents',
            'pageBreak',
            '|',

            // Advanced tools

            'findAndReplace',
            'selectAll',
            '|',

            // Import/Export
            'exportPdf',
            'exportWord',
            'importWord',
            '|',
          ],
        },
        licenseKey: LICENSE_KEY,
        placeholder: 'Type or paste your content here!',
        revisionHistory: {
          editorContainer: editorContainerRef.current,
          viewerContainer: editorRevisionHistoryRef.current,
          viewerEditorElement: editorRevisionHistoryEditorRef.current,
          viewerSidebarContainer: editorRevisionHistorySidebarRef.current,
          resumeUnsavedRevision: true,
        },
        cloudServices: {
          tokenUrl: '', // This can be empty for the LICENSE_KEY to work
          uploadUrl: '', // This can be empty for the LICENSE_KEY to work
        },
        exportWord: {
          converterUrl: `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/demands/template/${firmId}/export/`,
          stylesheets: [
            '/ckeditor-export-fixes.css',
            ...(demandTemplate?.stylesheet ? [demandTemplate.stylesheet] : []),
            'https://cdn.ckeditor.com/ckeditor5-premium-features/44.3.0/ckeditor5-premium-features.css',
            'https://cdn.ckeditor.com/ckeditor5/44.3.0/ckeditor5.css',
          ],
          dataCallback: (editor: { getData: (opts?: unknown) => string }) => {
            const raw = editor.getData({ pagination: true });
            const parser = new DOMParser();
            const doc = parser.parseFromString(raw, 'text/html');

            const { includeBatesCitations } = exportOptionsRef.current;

            console.log('[EXPORT DEBUG] dataCallback called');
            console.log('[EXPORT DEBUG] Export options:', {
              includeBatesCitations,
            });
            console.log('[EXPORT DEBUG] Raw HTML length:', raw.length);

            const citations = doc.querySelectorAll('cite[citation-id]');
            console.log(
              '[EXPORT DEBUG] Found citations in document:',
              citations.length,
            );

            citations.forEach((el, index) => {
              const citationId = el.getAttribute('citation-id');
              const batesAttr = el.getAttribute('bates-number');
              console.log(`[EXPORT DEBUG] Citation ${index}:`, {
                citationId,
                'bates-number': batesAttr,
                outerHTML: el.outerHTML,
              });

              if (includeBatesCitations) {
                const bates = batesAttr || '';
                if (bates) {
                  // Render the Bates number as visible text in the exported document
                  el.textContent = ` ${bates}`;
                  console.log(
                    `[EXPORT DEBUG] Set citation ${index} text to:`,
                    bates,
                  );
                  return;
                }
                el.textContent = ' [BATES NOT FOUND]';
                console.log(
                  `[EXPORT DEBUG] Citation ${index} has no bates, set to [BATES NOT FOUND]`,
                );
              } else {
                // Remove citation entirely when Bates citations are disabled
                el.textContent = '';
                console.log(
                  `[EXPORT DEBUG] Bates disabled, cleared citation ${index}`,
                );
              }
            });

            doc.querySelectorAll('span[citations]').forEach((el) => {
              if (includeBatesCitations) {
                const ids = el.getAttribute('citations') || '';
                el.append(` [CITATIONS:${ids}]`);
              } else {
                // Remove citation span content when disabled
                el.textContent = '';
              }
            });

            console.log(
              '[EXPORT DEBUG] Final HTML length:',
              doc.body.innerHTML.length,
            );
            return doc.body.innerHTML;
          },
          fileName: 'export-word.docx',
          converterOptions: {
            document: {
              orientation: 'portrait',
              size: 'A4',
              margin: {
                top: '20mm',
                bottom: '20mm',
                right: '12mm',
                left: '12mm',
              },
            },
          },
        },
        autosave: {
          saveInterval: 10_000,
          save: (editor: InstanceType<typeof _Editor>) => {
            const revisionTracker = editor.plugins.get(
              RevisionHistoryAutosaveIntegration,
            );
            return revisionTracker.autosave();
          },
        },
        documentId,
        finch_context: {
          matterId,
          documentId,
          workspaceId: workspaceId || undefined,
          registerPortalTarget,
        },

        revisionHistoryIntegration: {
          documentId,
        },
        commentsIntegration: {
          documentId,
        },
        trackChangesIntegration: {
          documentId,
        },

        pagination: {
          pageWidth: '210mm',
          pageHeight: '297mm',
          pageMargins: {
            top: '20mm',
            bottom: '20mm',
            right: '12mm',
            left: '12mm',
          },
        },
        table: {
          contentToolbar: [
            'tableColumn',
            'tableRow',
            'mergeTableCells',
            'tableProperties',
            'tableCellProperties',
            'toggleTableCaption',
          ],
        },
        image: {
          toolbar: [
            'imageStyle:inline',
            'imageStyle:block',
            'imageStyle:side',
            '|',
            'toggleImageCaption',
            'imageTextAlternative',
            '|',
            'linkImage',
          ],
        },
        fontFamily: {
          supportAllValues: true,
          options: [
            'default',
            'Arial, Helvetica, sans-serif',
            'Lato, sans-serif',
            'Courier New, Courier, monospace',
            'Georgia, serif',
            'Times New Roman, Times, serif',
          ],
        },
        fontSize: {
          options: [8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48],
          supportAllValues: true,
        },
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://',
        },
        wordCount: {
          displayWords: true,
          displayCharacters: true,
        },
        initialData: documentData?.initial_data || '<p>Start typing here!</p>',
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLayoutReady, cloud.status, demandTemplate, firmId, documentData]);

  // Keep export button in sync with eligibility and gating toggle
  useExportEligibilityEffect(
    editorRef,
    exportEligibility?.eligible,
    eligibilityGatingEnabled,
  );

  if (!areRequiredDependenciesReady) {
    return <div>Loading...</div>;
  }

  return (
    <div className="main-container">
      <div className="editor-container" ref={editorContainerRef}>
        <div className="editor-toolbar z-1" ref={editorToolbarRef} />
        {editorConfig && DecoupledEditor && (
          <CKEditor
            editor={DecoupledEditor}
            config={editorConfig as any}
            onReady={(editor) => {
              editorRef.current = editor;
              // Place the toolbar in our custom container
              const toolbarElement = editor.ui.view.toolbar?.element;
              if (editorToolbarRef.current && toolbarElement) {
                editorToolbarRef.current.appendChild(toolbarElement);
              }
              setEditor(editor);
              // Set the data if loaded after mount
              const revisionHistory = editor.plugins.get('RevisionHistory');

              // All revisions that have been loaded so far:
              const revisions = revisionHistory.getRevisions({ toJSON: false });
              // only inject initial_data when there are **no** revisions
              if (revisions.length === 2 && documentData?.initial_data) {
                editor.setData(documentData.initial_data);
              }

              // Disable exportWord until eligible
              try {
                const cmd = editor.commands.get('exportWord');
                if (
                  cmd &&
                  exportEligibility &&
                  exportEligibility.eligible === false
                ) {
                  cmd.forceDisabled('proofreading-eligibility');
                }
              } catch (e) {
                // ignore
              }

              // Intercept exportWord command to show options dialog first
              try {
                const exportWordCmd = editor.commands.get('exportWord');
                if (exportWordCmd) {
                  const originalExecute =
                    exportWordCmd.execute.bind(exportWordCmd);
                  let bypassDialog = false;

                  exportWordCmd.execute = async () => {
                    if (bypassDialog) {
                      // Reset flag and execute the actual export
                      bypassDialog = false;
                      return originalExecute();
                    }
                    // Show dialog instead of executing immediately
                    setShowExportDialog(true);
                    return Promise.resolve();
                  };

                  // Store setter for bypass flag
                  (exportWordCmd as any)._setBypassDialog = (
                    value: boolean,
                  ) => {
                    bypassDialog = value;
                  };
                }
              } catch (e) {
                // ignore
              }
            }}
            onAfterDestroy={() => {
              // Clean up toolbar elements when editor is destroyed
              if (editorToolbarRef.current) {
                while (editorToolbarRef.current.firstChild) {
                  editorToolbarRef.current.removeChild(
                    editorToolbarRef.current.firstChild,
                  );
                }
              }
              setEditor(null);
            }}
          />
        )}
      </div>
      <div
        className="revision-history-container"
        ref={editorRevisionHistoryRef}
      >
        <div className="revision-history-wrapper">
          <div
            className="revision-history-editor"
            ref={editorRevisionHistoryEditorRef}
          />
          <div
            className="revision-history-sidebar"
            ref={editorRevisionHistorySidebarRef}
          />
        </div>
      </div>
      {Object.entries(portalTargets).map(([, target]) => {
        return createPortal(target.node, target.target);
      })}

      <ExportOptionsDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onExport={handleExportWithOptions}
      />
    </div>
  );
};

// Reflect eligibility changes onto the command after mount
// Separate effect to avoid modifying CK config prematurely
// eslint-disable-next-line no-restricted-syntax
export function useExportEligibilityEffect(
  editorRef: React.MutableRefObject<any>,
  eligible?: boolean,
  gatingEnabled?: boolean,
) {
  const prev = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (!editorRef.current) return;
    // When gating is disabled, clear any forced disable and stop
    try {
      const cmd = editorRef.current.commands.get('exportWord');
      if (!cmd) return;
      if (!gatingEnabled) {
        cmd.clearForceDisabled('proofreading-eligibility');
        prev.current = eligible;
        return;
      }
    } catch {
      // noop
    }
    if (prev.current === eligible && gatingEnabled) return;
    try {
      const cmd = editorRef.current.commands.get('exportWord');
      if (!cmd) return;
      if (eligible) {
        cmd.clearForceDisabled('proofreading-eligibility');
      } else {
        cmd.forceDisabled('proofreading-eligibility');
      }
      prev.current = eligible;
    } catch {
      // noop
    }
  }, [eligible, gatingEnabled, editorRef]);
}
